import { useApiClient } from "../../../hooks/useApiClient";
import useSWR from "swr";
import {
  type ICommercialFeedFetchParams,
  type ICommercialFeedResponse,
  type ICommercialFeedItem,
} from "../types";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";

interface IUseCommercialFeedReturnType {
  feed: ICommercialFeedResponse | undefined;
  allCount: number;
  from: number;
  prev: () => void;
  next: () => void;
  isLoading: boolean;
  isError: boolean;
}

interface IUseCommercialFeedOptions {
  enabled?: boolean;
}

// useCommercialFeedに渡すパラメータの型
type IUseCommercialFeedParams = Omit<
  ICommercialFeedFetchParams,
  "size" | "fromCount"
> & {
  size: number;
};

type SwrKey = [string, ICommercialFeedFetchParams];

/**
 * 検索パラメータからsize以外の検索条件を抽出してJSON文字列に変換する
 * パラメータの変更検知に使用
 */
const getSearchParamsKey = (params: IUseCommercialFeedParams): string => {
  const { size: _, ...searchParams } = params;
  return JSON.stringify(searchParams);
};

/**
 * 商業登記受付帳フィードを取得するカスタムフック
 * @param params - 検索パラメータ
 * @param options - オプション（enabled: falseの場合はAPIをリクエストしない）
 */
export const useCommercialFeed = (
  params: IUseCommercialFeedParams,
  options: IUseCommercialFeedOptions = { enabled: true }
): IUseCommercialFeedReturnType => {
  const { apiClient } = useApiClient();
  const { enabled = true } = options;

  // 何件目から取得するか。SQLでいうところのoffset
  const [from, setFrom] = useState<number>(0);
  // 累積データを保持するstate
  const [accumulatedList, setAccumulatedList] = useState<ICommercialFeedItem[]>(
    []
  );
  // 前回の検索パラメータを保持
  const prevSearchParamsKeyRef = useRef<string>("");

  // 検索パラメータが変わったらfromを0にリセットし、累積データをクリア
  const currentSearchParamsKey = getSearchParamsKey(params);
  useEffect(() => {
    if (
      prevSearchParamsKeyRef.current !== "" &&
      prevSearchParamsKeyRef.current !== currentSearchParamsKey
    ) {
      setFrom(0);
      setAccumulatedList([]);
    }
    prevSearchParamsKeyRef.current = currentSearchParamsKey;
  }, [currentSearchParamsKey]);

  const fetcher = async (args: SwrKey): Promise<ICommercialFeedResponse> => {
    const [url, reqParams] = args;
    const { data } = await apiClient.post<ICommercialFeedResponse>(
      url,
      reqParams
    );
    return data;
  };

  const fetchParams: ICommercialFeedFetchParams = {
    ...params,
    size: params.size,
    fromCount: from,
  };

  // enabledがfalseの場合はSWRのキーをnullにしてリクエストを無効化
  const swrKey: SwrKey | null = enabled
    ? ["commercial-reception-book/feed", fetchParams]
    : null;

  const { data, error, isLoading } = useSWR<
    ICommercialFeedResponse,
    Error,
    SwrKey | null
  >(swrKey, fetcher, { revalidateOnFocus: false });

  // 新しいデータが取得されたら累積リストを更新
  useEffect(() => {
    if (data === undefined) return;

    if (from === 0) {
      // fromが0の場合は新しい検索なので、リストを置き換え
      setAccumulatedList(data.list);
    } else {
      // fromが0以外の場合は、データを累積
      setAccumulatedList((prev) => {
        // 重複を避けるため、fromの位置に基づいてデータを追加
        const newList = [...prev];
        // fromの位置から新しいデータを配置
        data.list.forEach((item, index) => {
          newList[from + index] = item;
        });
        return newList;
      });
    }
  }, [data, from]);

  // 累積データを含むfeedオブジェクトを生成
  const accumulatedFeed: ICommercialFeedResponse | undefined = useMemo(() => {
    if (data === undefined && accumulatedList.length === 0) return undefined;
    return {
      list: accumulatedList,
      count: data?.count ?? 0,
    };
  }, [accumulatedList, data]);

  const prev = useCallback((): void => {
    setFrom((currentFrom) => {
      if (currentFrom === 0) return currentFrom;
      return currentFrom - params.size;
    });
  }, [params.size]);

  const next = useCallback((): void => {
    if (data === undefined) return;

    const { size } = params;
    const { count } = data;
    setFrom((currentFrom) => {
      if (currentFrom + size >= count) return currentFrom;
      return currentFrom + size;
    });
  }, [data, params.size]);

  return {
    feed: accumulatedFeed,
    allCount: data?.count ?? 0,
    from,
    prev,
    next,
    isLoading,
    isError: Boolean(error),
  };
};
